import * as React from 'react'
import {useParams} from 'react-router'
import {H1} from '@frontrow/console/src/components/typography'
import {fromJust, maybe} from '@freckle/maybe'
import {isSchoolCrmSynced} from '@frontrow/console/src/entities/models/school'
import {SpinnerOverlay} from '@frontrow/console/src/components/spinner'

import {CrmManagedAlert} from '@frontrow/console/src/components/common/crm-managed-alert'
import ErrorAlert from '@frontrow/console/src/components/common/error-alert'
import PanelList from '@frontrow/console/src/components/common/panel-list'
import {type BasicSchoolWithRenaissanceClientT} from '@frontrow/console/src/models/school'
import {detailedToBasicSchool} from '@frontrow/console/src/models/school'
import SchoolLicensesList from '@frontrow/console/src/components/common/school-licenses-list'
import {type SchoolLicensesListSchoolProps} from '@frontrow/console/src/components/common/school-licenses-list'
import {schoolLicensesContainer} from './school-licenses.module.scss'
import {SyncUpdatesButton} from '@frontrow/console/src/components/common/school-actions-section/sync-updates-button'
import {useSchool, useUpdateSchool} from '@frontrow/console/src/hooks/use-school'

export default function SchoolLicensesContainer(): React.ReactElement {
  const schoolId = parseInt(
    fromJust(useParams<{schoolId: string}>().schoolId, 'Cannot find schoolId in URL'),
    10
  )

  const {school, isLoading, error} = useSchool(schoolId)
  const {updateSchool} = useUpdateSchool()

  const dispatchUpdateSchool = (
    schoolId: number,
    updatedSchool: BasicSchoolWithRenaissanceClientT
  ) => updateSchool(schoolId, updatedSchool)

  function setCourseSubjectsByLicensing(courseSubjectsByLicensing: boolean) {
    if (school !== null && school !== undefined) {
      const basicSchool = detailedToBasicSchool(school)

      const updatedSchoolData = {
        ...basicSchool,
        courseSubjectsByLicensing
      }

      dispatchUpdateSchool(schoolId, updatedSchoolData)
    }
  }
  function renderSchoolLicensesList(
    schoolData: SchoolLicensesListSchoolProps,
    _: number
  ): React.ReactElement {
    return (
      <SchoolLicensesList
        key={schoolData.schoolId}
        {...schoolData}
        courseSubjectsByLicensing={maybe(
          () => false,
          school => school.courseSubjectsByLicensing,
          school
        )}
        // eslint-disable-next-line react/jsx-no-bind
        setCourseSubjectsByLicensing={setCourseSubjectsByLicensing}
      />
    )
  }

  const title =
    school !== undefined && school !== null
      ? `Editing ${school.name} Licenses`
      : 'No School Information'

  const syncedSchoolData =
    school !== undefined && school !== null
      ? [
          {
            schoolId: school.id,
            schoolName: school.name,
            enableModifications: !isSchoolCrmSynced({
              active: school.active,
              crmId: school.crmId
            })
          }
        ]
      : []

  const errorNode = error !== null && error !== undefined ? <ErrorAlert>{error}</ErrorAlert> : null

  const body = isLoading ? (
    <SpinnerOverlay />
  ) : (
    <PanelList
      // eslint-disable-next-line react/jsx-no-bind
      renderListElement={renderSchoolLicensesList}
      dataList={syncedSchoolData}
    />
  )

  const syncUpdatesButton = maybe(
    () => null,
    clientId => (school?.active ? <SyncUpdatesButton clientId={clientId} /> : null),
    school?.renaissanceSchoolClient?.clientId
  )

  return (
    <React.Fragment>
      <H1>{title}</H1>
      {errorNode}
      <CrmManagedAlert
        active={!!school?.active}
        crmId={school?.crmId}
        crmManagedEntity="school"
        instructions="To update licensing, make changes in Renaissance Admin tools"
      />

      {syncUpdatesButton}

      <div className={schoolLicensesContainer}>{body}</div>
    </React.Fragment>
  )
}
